import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  UserCircleIcon,
  CurrencyDollarIcon,
  MapPinIcon,
  BuildingOfficeIcon,
  PencilIcon,
  StarIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/react/24/outline';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const API_BASE_URL = API_URL.replace(/\/api$/, '');

const Profile = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [agreementPreviewOpen, setAgreementPreviewOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propertyAddress: ''
  });

  useEffect(() => {
    if (user?.role === 'merchant') {
      navigate('/merchant/dashboard', { replace: true });
      return;
    }
    fetchProfileData();
  }, [user]);

  const fetchProfileData = async () => {
    try {
      const response = await axios.get(`${API_URL}/users/profile`);
      setProfileData(response.data);
      setFormData({
        name: response.data.name || '',
        email: response.data.email || '',
        phone: response.data.phone?.replace('+91', '') || '',
        propertyAddress: response.data.propertyAddress || ''
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
      setProfileData(user);
      setFormData({
        name: user?.name || '',
        email: user?.email || '',
        phone: user?.phone?.replace('+91', '') || '',
        propertyAddress: user?.propertyAddress || ''
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/users/profile`, formData);
      await fetchProfileData();
      setEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 via-white to-primary-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const profile = profileData || user;
  const points = Math.round(profile?.currentRewardBalance || 0);
  const totalEarned = Math.round(profile?.totalRewardsEarned || 0);
  const rentAgreementUrl = profile?.rentAgreementUrl
    ? (profile.rentAgreementUrl.startsWith('http')
      ? profile.rentAgreementUrl
      : `${API_BASE_URL}${profile.rentAgreementUrl}`)
    : null;
  const isAgreementPdf = rentAgreementUrl
    ? rentAgreementUrl.toLowerCase().includes('.pdf')
    : false;

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-50 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-primary-500 to-primary-700 bg-clip-text text-transparent mb-2">
            Your Profile
          </h1>
          <p className="text-gray-600 text-lg">Manage your account and track your rewards</p>
        </div>

        {/* Points Wallet Card */}
        <div className="relative bg-gradient-to-br from-primary-500 via-primary-600 to-primary-700 rounded-2xl shadow-2xl p-6 text-white overflow-hidden mb-8">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
          <div className="relative">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm opacity-90 mb-1">LOCU Points</p>
                <p className="text-4xl font-bold">{points.toLocaleString('en-IN')}</p>
              </div>
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                <StarIcon className="h-8 w-8" />
              </div>
            </div>
            <div className="pt-4 border-t border-white/20">
              <p className="text-xs opacity-80 mb-1">Total Earned</p>
              <p className="text-xl font-bold">{totalEarned.toLocaleString('en-IN')}</p>
            </div>
          </div>
        </div>

        {/* Profile Information - Enhanced */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-6 border border-gray-100">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Profile Information</h2>
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-end">
              {rentAgreementUrl && (
                <button
                  type="button"
                  onClick={() => {
                    if (isAgreementPdf) {
                      window.open(rentAgreementUrl, '_blank', 'noopener,noreferrer');
                    } else {
                      setAgreementPreviewOpen(true);
                    }
                  }}
                  className="inline-flex items-center justify-center px-4 py-2 bg-white border-2 border-primary-200 text-primary-700 rounded-xl font-semibold hover:border-primary-300 hover:text-primary-800 transition-all"
                >
                  View Rent Agreement
                </button>
              )}
              {!editing && (
                <button
                  onClick={() => setEditing(true)}
                  className="inline-flex items-center px-5 py-2.5 bg-gradient-to-r from-primary-500 to-primary-700 text-white rounded-xl font-semibold hover:shadow-lg transition-all transform hover:scale-105"
                >
                  <PencilIcon className="h-5 w-5 mr-2" />
                  Edit Profile
                </button>
              )}
            </div>
          </div>

          {editing ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="10-digit phone number"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Property Address</label>
                  <textarea
                    name="propertyAddress"
                    value={formData.propertyAddress}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                  />
                </div>
              </div>

              <div className="flex space-x-4 pt-4">
                <button
                  type="submit"
                  className="px-8 py-3 bg-gradient-to-r from-primary-500 to-primary-700 text-white rounded-xl font-bold hover:shadow-lg transition-all transform hover:scale-105"
                >
                  Save Changes
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditing(false);
                    fetchProfileData();
                  }}
                  className="px-8 py-3 border-2 border-gray-300 rounded-xl font-semibold text-gray-700 hover:bg-gray-50 transition-all"
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-4 bg-gradient-to-r from-primary-50 to-primary-100 rounded-xl border border-blue-100">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary-400 to-primary-500 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <UserCircleIcon className="h-6 w-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-600 mb-1">Name</p>
                    <p className="text-lg font-bold text-gray-900">{profile?.name || 'Not set'}</p>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-gradient-to-r from-primary-50 to-primary-100 rounded-xl border border-primary-100">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary-400 to-primary-500 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <UserCircleIcon className="h-6 w-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-600 mb-1">Email</p>
                    <p className="text-lg font-bold text-gray-900">{profile?.email || 'Not set'}</p>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-100">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-400 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <UserCircleIcon className="h-6 w-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-600 mb-1">Phone</p>
                    <p className="text-lg font-bold text-gray-900">{profile?.phone || 'Not set'}</p>
                  </div>
                </div>
              </div>

              {profile?.propertyAddress && (
                <div className="p-4 bg-gradient-to-r from-orange-50 to-red-50 rounded-xl border border-orange-100">
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-red-400 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                      <MapPinIcon className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-600 mb-1">Property Address</p>
                      <p className="text-lg font-bold text-gray-900">{profile.propertyAddress}</p>
                    </div>
                  </div>
                </div>
              )}

              {profile?.monthlyRent && profile.monthlyRent > 0 && (
                <div className="p-4 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl border border-amber-100 md:col-span-2">
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-yellow-400 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                      <BuildingOfficeIcon className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-600 mb-1">Monthly Rent</p>
                      <p className="text-2xl font-bold text-gray-900">₹{profile.monthlyRent.toLocaleString('en-IN')}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Quick Links - Enhanced */}
        <div className="grid md:grid-cols-2 gap-6">
          <Link
            to="/pay-rent"
            className="group bg-white rounded-2xl shadow-xl p-6 hover:shadow-2xl transition-all border-2 border-primary-100 hover:border-primary-300 transform hover:-translate-y-1"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">Pay Rent</h3>
                <p className="text-sm text-gray-600">Earn 2% LOCU points instantly</p>
              </div>
              <div className="w-16 h-16 bg-gradient-to-br from-primary-100 to-primary-200 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <CurrencyDollarIcon className="h-8 w-8 text-primary-600" />
              </div>
            </div>
          </Link>

          <Link
            to="/transactions"
            className="group bg-white rounded-2xl shadow-xl p-6 hover:shadow-2xl transition-all border-2 border-primary-100 hover:border-primary-300 transform hover:-translate-y-1"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">Transaction History</h3>
                <p className="text-sm text-gray-600">View all your payments</p>
              </div>
              <div className="w-16 h-16 bg-gradient-to-br from-primary-100 to-primary-200 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <CurrencyDollarIcon className="h-8 w-8 text-primary-600" />
              </div>
            </div>
          </Link>
        </div>

        {/* Logout Section */}
        <div className="mt-8 bg-white rounded-2xl shadow-xl p-6 border-2 border-red-100">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Account Actions</h3>
              <p className="text-sm text-gray-600">Sign out from your account</p>
            </div>
            <button
              onClick={() => {
                if (window.confirm('Are you sure you want to logout?')) {
                  logout();
                  navigate('/');
                }
              }}
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl font-semibold hover:shadow-lg transition-all transform hover:scale-105"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5 mr-2" />
              Logout
            </button>
          </div>
        </div>

      </div>

      {/* Rent Agreement Preview */}
      {agreementPreviewOpen && rentAgreementUrl && !isAgreementPdf && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-auto">
            <button
              type="button"
              onClick={() => setAgreementPreviewOpen(false)}
              className="absolute top-3 right-3 w-10 h-10 rounded-full bg-white/90 text-gray-700 hover:text-gray-900 flex items-center justify-center shadow"
              aria-label="Close"
            >
              ✕
            </button>
            <div className="p-4">
              <img
                src={rentAgreementUrl}
                alt="Rent agreement"
                className="w-full h-auto rounded-lg"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
